import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { toast } from "react-toastify";
import { Authorization } from "../../../Context/Authorization";
import Select from "react-select";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const cellInput = { width: "100%", padding: "5px 8px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12, outline: "none" };

const GroupLoanRepayment = () => {
  const [vfcNames, setVfcNames] = useState([]);
  const [loanNumbers, setLoanNumbers] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [selectedLoan, setSelectedLoan] = useState("");
  const [tableData, setTableData] = useState([]);
  const [totalLoanAmount, setTotalLoanAmount] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const Api = process.env.REACT_APP_API;
  const [selectedDate, setSelectedDate] = useState("");

  const fetchVfcNames = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, config);
      const data = response.data.vfcs;
      if (Array.isArray(data)) setVfcNames(data);
      else console.error("API did not return an array", data);
    } catch (error) {
      console.log("Error fetching Data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcNames(selectedJfmcType._id);
  }, [selectedJfmcType]);

  // fetchVfcNames is called via useEffect when selectedJfmcType changes (handled by parent context)

  const handleVfcChange = async (event) => {
    const vfcId = event.target.value;
    setSelectedVfc(vfcId);
    setLoanNumbers([]);
    setSelectedLoan("");
    setTableData([]);
    setShowTable(false);
    setError(null);
    setSuccess(null);
    setLoading(true);
    if (vfcId) {
      try {
        const config = Authorization();
        const response = await fetch(
          `${Api}/api/loans/getLoanByVfcId`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...config.headers,
            },
            body: JSON.stringify({
              vfcId,
              groupType: "Group",
            }),
          }
        );
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        const loans = data.loans || [];
        if (Array.isArray(loans)) setLoanNumbers(loans.filter((loan) => loan.groupType === "Group"));
        else throw new Error("Invalid data format: Expected an array of loans.");
      } catch (error) {
        console.error("Error:", error);
        setError(`Failed to fetch loan numbers. ${error.message}`);
      }
    }
    setLoading(false);
  };

  const handleLoanChange = async (event) => {
    const loanNo = event.target.value;
    setSelectedLoan(loanNo);
    setTableData([]);
    setShowTable(false);
    setError(null);
    setSuccess(null);
    setLoading(true);
    if (loanNo) {
      const selectedLoanData = loanNumbers.find((loan) => loan._id === loanNo);
      if (!selectedLoanData) { setLoading(false); throw new Error("Loan data not found"); }
      try {
        const config = Authorization();
        const response = await fetch(`${Api}/api/loans/getLoanByLoanNo`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...config.headers },
          body: JSON.stringify({ loanNo: selectedLoanData.loanNo, groupType: "Group", }),
        });
        if (!response.ok) throw new Error("Error fetching loan details");
        const data = await response.json();
        if (data.groupType === "Group") {
          const formattedData = data.subLoans.map((subLoan) => ({
            ...subLoan,
            loanNumber: data.loanNo,
            loanAmount: subLoan.disbursedAmount,
            name: subLoan.memberId ? subLoan.memberId.name : "Unknown",

            totalPaid: subLoan.paidAmount ?? 0,
            balance: subLoan.balance ?? subLoan.disbursedAmount,

            originalBalance: subLoan.balance ?? subLoan.disbursedAmount,

            principal: 0,
            interest: 0,
            total: 0,
          }));
          setTableData(formattedData);
          setTotalLoanAmount(data.amount);
        } else {
          setError("The selected loan does not have any sub-loans.");
        }
      } catch (error) {
        console.log(error.message);
        setError("Failed to fetch loan details. Please try again.");
      }
    }
    setLoading(false);
  };

  const loanOptions = loanNumbers.map((loan) => ({
    value: loan._id,
    label: `${loan.loanNo} - ${loan.shgId?.name || "No SHG"}`
  }));

  const handleInputChange = (index, field, value) => {
    const newTableData = [...tableData];

    let val = field === "paymentMode" ? value : parseFloat(value) || 0;

    const originalBalance = newTableData[index].originalBalance || 0;

    // ✅ PREVENT OVERPAYMENT
    if (field === "principal" && val > originalBalance) {
      val = originalBalance;
      toast.warning("Cannot enter more than balance");
    }

    newTableData[index][field] = val;

    if (field === "principal" || field === "interest") {
      const principal = parseFloat(newTableData[index].principal) || 0;
      const interest = parseFloat(newTableData[index].interest) || 0;

      newTableData[index].total = principal + interest;

      newTableData[index].balance = originalBalance - principal;
    }

    setTableData(newTableData);
  };

  const handleSubmit = async () => {
    try {
      const payload = tableData.map((loan) => ({
        loanId: selectedLoan,
        subLoanId: loan._id,
        amountPaid: loan.principal,
        memberId: loan.memberId,
        interestPaid: loan.interest,
        cashOrBank: loan.paymentMode,
        paymentDate: selectedDate,
      }));
      const config = Authorization();
      const response = await fetch(`${Api}/api/loan-collections`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...config.headers },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Error saving loan collections");
      setSuccess("Loan collections saved successfully");
      toast.success("Added Successfully");
      await logActivity(userId, "New GroupLoan Repayment Added", "Group Loan Repayment Page", Api);
      setError(null);
      setShowTable(false);
      setTableData([]);
      setSelectedLoan("");
    } catch (error) {
      console.log(error.message);
      setError("Failed to save loan collections. Please try again.");
      setSuccess(null);
    }
  };

  const handleShowTable = () => {
    if (selectedLoan) setShowTable(true);
    else setError("Please select a loan number first.");
  };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Select Loan</span>
            </div>

            {error && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-alert-circle-outline" /> {error}
              </div>
            )}
            {success && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#E8F5E9", border: "1px solid #C8E6C9", borderRadius: 8, fontSize: 13, color: "#1B5E20", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-check-circle-outline" /> {success}
              </div>
            )}

            {showTable && totalLoanAmount > 0 && (
              <div style={{ marginBottom: 14, padding: "8px 16px", background: "#E8F5E9", borderRadius: 8, border: "1px solid #C8E6C9", fontSize: 13, color: "#1B5E20" }}>
                <strong>Total Loan Amount:</strong> ₹{totalLoanAmount}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>JFMC</label>
                <Select
                  options={vfcNames.map((v) => ({ value: v._id, label: v.vfcName }))}
                  value={vfcNames.map((v) => ({ value: v._id, label: v.vfcName })).find((option) => option.value === selectedVfc) || null}
                  onChange={(option) => handleVfcChange({ target: { value: option?.value || "" } })}
                  placeholder="Select JFMC"
                  isClearable
                />
              </div>
              <div>
                <label style={labelStyle}>Loan No</label>
                <Select
                  options={loanOptions}
                  value={loanOptions.find((o) => o.value === selectedLoan) || null}
                  onChange={(option) => handleLoanChange({ target: { value: option?.value || "" } })}
                  placeholder="Select Loan Number"
                  isDisabled={!selectedVfc}
                  isClearable
                />
              </div>
              <div>
                <label style={labelStyle}>Repayment Date</label>
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
              </div>
            </div>

            <button onClick={handleShowTable}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
              <i className="mdi mdi-file-check" /> Show Loan Details
            </button>
          </div>

          {/* ── Table Card ── */}
          {showTable && (
            <>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                  <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                    <i className="mdi mdi-account-group-outline" />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Group Loan Details</span>
                  <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{tableData.length} members</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["S.No", "Name", "Loan No", "Amount", "Balance", "Principal", "Interest", "Payment Mode", "Total"].map((h) => (
                          <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "#4A5568", borderBottom: "1px solid #EDF2F7", background: "#F7FAFC", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((loan, index) => (
                        <tr key={loan._id} style={{ borderBottom: "1px solid #EDF2F7" }}>
                          <td style={{ padding: "10px 14px", color: "#718096" }}>{index + 1}</td>
                          <td style={{ padding: "10px 14px", color: "#1A202C", fontWeight: 600 }}>{loan.name}</td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.subLoanId}</td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.loanAmount}</td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.balance}</td>
                          <td style={{ padding: "8px 10px" }}>
                            <input type="number" value={loan.principal} disabled={loan.balance <= 0}   // ✅ ADD THIS
                              onChange={(e) => handleInputChange(index, "principal", e.target.value)} style={cellInput}
                              onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                          </td>
                          <td style={{ padding: "8px 10px" }}>
                            <input type="number" value={loan.interest} disabled={loan.balance <= 0} onChange={(e) => handleInputChange(index, "interest", e.target.value)} style={cellInput}
                              onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                          </td>
                          <td style={{ padding: "8px 10px" }}>
                            <select value={loan.paymentMode || ""} onChange={(e) => handleInputChange(index, "paymentMode", e.target.value)} style={cellInput}>
                              <option value="">Select</option>
                              <option value="cash">Cash</option>
                              <option value="bank">Bank</option>
                            </select>
                          </td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button onClick={handleSubmit}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
                <i className="mdi mdi-file-check" /> Submit
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GroupLoanRepayment;
